import { LayoutsType } from 'external-services/layouts-service/layouts.type';
import {
  IDiaryService,
  ILayout,
  ILayoutMinimalAbstract,
  IObjectMap,
  IPouchRows,
  ISectionLayoutItem,
  ITableItem,
} from 'shared-types/index';
import TablesLookupService from 'external-services/tables-lookup-service/tablesLookup.service';
import DateUtilsService from 'external-services/date-utils-service';
import {
  ILoginActions,
  IPouchActions,
  IVenueActions,
  PouchDocType,
  VenueActionNames,
} from 'shared-types/ActionsTypes';
import PouchLoadService from 'external-services/pouch-load-service/index';
import { cloneDeep, orderBy } from 'lodash-es';

class LayoutsService {
  private tablesByLayoutCache: IObjectMap<ITableItem>;
  private layoutGroups: IObjectMap<ILayoutMinimalAbstract>;

  public setup() {
    this.tablesByLayoutCache = {};
  }
  public init(
    LoginActions: ILoginActions,
    VenueActions: IVenueActions,
    PouchActions: IPouchActions
  ) {
    PouchActions.handlePouchSyncedDoctype(PouchDocType.layout, () => this.clearCache());
    PouchActions.handlePouchSyncedDoctype(PouchDocType.table, () => this.clearCache());
    LoginActions.handleLogout(() => this.clearCache());
    VenueActions.handleVenueAction(VenueActionNames.VENUE_CHANGE, () => this.clearCache());
  }
  private clearCache() {
    this.layoutGroups = null;
    this.tablesByLayoutCache = {};
  }
  /**
   * Returns a layout by id
   * @return {Promise}
   * @param id
   */
  public getLayoutById(id: string): Promise<ILayout> {
    return PouchLoadService.getDbLocal().get(id);
  }
  /**
   * Get all the tables by layout ids
   * @param {Array} layoutIds
   * @param date
   * @param returnRef
   * @return {Array}
   */
  public async getByLayouts(
    layoutIds: string[],
    date: Date,
    returnRef: any = null
  ): Promise<{ tablesArray: ITableItem[]; returnRef: any }> {
    const cacheKey =
      layoutIds.join('-') + '-' + date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate();

    if (this.tablesByLayoutCache[cacheKey]) {
      return {
        tablesArray: cloneDeep(this.tablesByLayoutCache[cacheKey]), // add cloneDeep in case any redux value save it
        returnRef: returnRef,
      };
    }

    const cleanLayoutIds: string[] = layoutIds.filter((id) => !!id);
    // find all the valid layouts for this date
    const layoutsArray = await this.getLayouts(cleanLayoutIds, date);
    const tableIds: string[] = layoutsArray.reduce((acc, layout) => [...acc, ...layout.tables], []);
    let tablesArray = await TablesLookupService.getAllTablesByIds(
      tableIds,
      this.tablesByLayoutCache,
      cacheKey
    );
    tablesArray = orderBy(tablesArray, ['name']);
    return {
      tablesArray: tablesArray,
      returnRef: returnRef,
    };
  }
  /**
   * Gets the correct version of a layout for a given date.
   */
  public getAllLayouts(): Promise<ILayout[]> {
    return PouchLoadService.getDbLocal()
      .allDocs({
        include_docs: true,
        attachments: false,
        startkey: 'layout_',
        endkey: 'layout_\uffff',
      })
      .then((layouts: unknown) => {
        // Check if the layouts are valid
        const selectedLayouts: { [key: string]: { layout: ILayout; lastValidDate: Date } } = {};
        (layouts as LayoutsType).rows.forEach((row) => {
          const layout = row.doc;
          if (!layout.hidden) {
            const idParts = layout._id.split('_');
            const dateStr = idParts[idParts.length - 1];
            const validFrom = DateUtilsService.dateStrToDate(dateStr, '-');
            idParts.pop();
            const layoutId = idParts.join('_');
            if (!selectedLayouts[layoutId]) {
              selectedLayouts[layoutId] = {
                layout: null,
                lastValidDate: new Date(1900, 1, 1),
              };
            }
            if (validFrom.getTime() > selectedLayouts[layoutId].lastValidDate.getTime()) {
              selectedLayouts[layoutId].layout = layout;
              selectedLayouts[layoutId].lastValidDate = validFrom;
            }
          }
        });

        const layoutsList: ILayout[] = [];
        for (const id in selectedLayouts) {
          layoutsList.push(selectedLayouts[id].layout);
        }
        if (layoutsList.length) {
          return layoutsList;
        }
        throw 'no valid layouts found';
      });
  }
  /**
   * Get all the layoout by layout ids
   * @param {Array} layoutIds
   * @param date
   * @return {Array}
   */
  public getLayouts(layoutIds: string[], date: Date): Promise<ILayoutMinimalAbstract[]> {
    const promises: Promise<ILayoutMinimalAbstract>[] = layoutIds.reduce((acc, layoutId) => {
      if (layoutId) {
        acc.push(this.getLayoutByDate(layoutId, date));
      }
      return acc;
    }, []);
    return Promise.all(promises);
  }

  /**
   * Gets all layouts used in the current service
   */
  public getLayoutIds(service: IDiaryService): string[] {
    if (service) {
      const layoutIds: string[] = service.sectionLayouts.reduce((acc, sectionLayout) => {
        if (sectionLayout.layoutId) {
          acc.push(sectionLayout.layoutId);
        }
        return acc;
      }, []);

      return layoutIds;
    } else {
      return [];
    }
  }

  /**
   * Gets all Section ids in the layouts used in the current service
   */
  public getSectionIds(sectionLayouts: ISectionLayoutItem[]): string[] {
    if (sectionLayouts) {
      return sectionLayouts.reduce((acc, sectionLayout) => {
        if (!sectionLayout.sectionClosed && !sectionLayout.sectionNotUsed && sectionLayout.sectionId) {
          acc.push(sectionLayout.sectionId);
        }
        return acc;
      }, []);
    } else {
      return [];
    }
  }

  public getLayoutBasedOnId = (layouts: ILayout[], layoutId: string):ILayout  => {
      return layouts.find((layout) => {
          const layoutIdParts = layout._id.split('_');
          layoutIdParts.pop();
          const id = layoutIdParts.join('_');
          return id === layoutId;
      });
  };

  public getLayoutIdWithoutDate(layoutId: string): string {
    const layoutIdParts = layoutId.split('_');
    layoutIdParts.pop();
    const id = layoutIdParts.join('_');
    return id;
  }

  /**
   * Gets the correct version of a layout for a given date.
   */
  private getLayoutByDate(layoutId: string, date: Date): Promise<ILayoutMinimalAbstract> {
    return this.getLayoutGroup(layoutId).then((layouts: ILayoutMinimalAbstract[]) => {
      // Check if the schedules are vaild
      let selectedLayout: ILayoutMinimalAbstract;
      let lastValidDate: Date;
      layouts &&
        layouts.forEach((layout) => {
          const idParts: string[] = layout._id.split('_');
          const dateStr: string = idParts[idParts.length - 1];
          const validFrom: Date = DateUtilsService.dateStrToDate(dateStr, '-');
          if (date.getTime() >= validFrom.getTime()) {
            if (!lastValidDate || validFrom.getTime() > lastValidDate.getTime()) {
              selectedLayout = layout;
              lastValidDate = validFrom;
            }
          }
        });
      if (selectedLayout) {
        return selectedLayout;
      }
      throw 'no valid layout found';
    });
  }

  private getLayoutGroup(layoutId: string): Promise<ILayoutMinimalAbstract[]> {
    return this.getLayoutsGroups().then((lg) => {
      return lg[layoutId];
    });
  }
  private async getLayoutsGroups(): Promise<IObjectMap<ILayoutMinimalAbstract>> {
    if (this.layoutGroups) {
      return this.layoutGroups;
    }
    // load all the layouts and group them
    const layouts = await this.loadAllLayouts();
    return this.groupLayouts(layouts);
  }
  private groupLayouts(
    layouts: IPouchRows<ILayoutMinimalAbstract>
  ): IObjectMap<ILayoutMinimalAbstract> {
    const layoutGroups: IObjectMap<ILayoutMinimalAbstract> = {};
    layouts.rows.forEach((row) => {
      const layout = row.doc;
      const layoutIdParts = layout._id.split('_');
      layoutIdParts.pop();
      const layoutId = layoutIdParts.join('_');
      if (!layoutGroups[layoutId]) {
        layoutGroups[layoutId] = [];
      }
      layoutGroups[layoutId].push(layout);
    });
    this.layoutGroups = layoutGroups;
    return this.layoutGroups;
  }
  private loadAllLayouts(): Promise<any> {
    return PouchLoadService.getDbLocal().allDocs({
      include_docs: true,
      attachments: false,
      startkey: 'layout_',
      endkey: 'layout_\uffff',
    });
  }
}
const instance = new LayoutsService();
export default instance as LayoutsService;
